import React, { useState, useEffect } from 'react';
import { Button, Typography, Container, Grid, Box, IconButton } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import SettingsIcon from '@mui/icons-material/Settings';
import IdentifyNumbers from '../components/activities/IdentifyNumbers';
import CountObjects from '../components/activities/CountObjects';
import FindMissingNumber from '../components/activities/FindMissingNumber';
import SortNumbers from '../components/activities/SortNumbers';
import MatchPairs from '../components/activities/MatchPairs';
import SortDominos from '../components/activities/SortDominos';
import IdentifyShapes from '../components/activities/IdentifyShapes';
import IdentifyColors from '../components/activities/IdentifyColors';
import FindOddOneOut from '../components/activities/FindOddOneOut';

const activities = [
  { id: 'identifyNumbers', title: 'Identifier les Nombres', component: IdentifyNumbers, color: '#FF5733', emoji: '🧐' }, // Magnifier glass for identifying numbers
  { id: 'countObjects', title: 'Compter des Objets', component: CountObjects, color: '#33B5E5', emoji: '🧮' }, // Abacus for counting
  { id: 'findMissingNumber', title: 'Nombre Manquant', component: FindMissingNumber, color: '#FF9800', emoji: '❓' }, // Question mark for missing number
  { id: 'sortNumbers', title: 'Réorganisez les Nombres', component: SortNumbers, color: '#4CAF50', emoji: '🔀' }, // Shuffle for resorting
  { id: 'matchPairs', title: 'Nombres et Dominos', component: MatchPairs, color: '#9C27B0', emoji: '🃏' }, // Joker card for matching
  { id: 'sortDominos', title: 'Réorganisez les Dominos', component: SortDominos, color: '#FFC107', emoji: '🎲' }, // Dice for dominos
  { id: 'identifyShapes', title: 'Reconnaître les Formes', component: IdentifyShapes, color: '#607D8B', emoji: '🔺' }, // Triangle for shapes
  { id: 'identifyColors', title: 'Identifier les Couleurs', component: IdentifyColors, color: '#E91E63', emoji: '🎨' }, // Paint palette for colors
  { id: 'findOddOneOut', title: 'Trouvez l\'Intrus', component: FindOddOneOut, color: '#795548', emoji: '🕵️‍♂️' }, // Detective for finding the odd one
];


function getGridLayout(activityCount) {
  const sqrt = Math.floor(Math.sqrt(activityCount));
  let rows, cols;

  if (sqrt * sqrt === activityCount) {
    rows = cols = sqrt;
  } else {
    rows = sqrt;
    cols = Math.ceil(activityCount / rows);
  }

  return { rows, cols };
}

function ActivityPage({ settings, onSettingsClick }) {
  const [currentActivity, setCurrentActivity] = useState(null);
  const [score, setScore] = useState(0);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);

  // Log score changes
  useEffect(() => {
    console.log('Score updated:', score);
  }, [score]);

  const incrementScore = () => {
    console.log('Incrementing score from:', score);
    setScore(prevScore => {
      const newScore = prevScore + 1;
      console.log('New score will be:', newScore);
      return newScore;
    });
  };

  const handleStartSession = () => {
    console.log('Starting new session');
    setScore(0);
    setSessionActive(true);
    setSessionEnded(false);
  };

  const handleSessionEnd = () => {
    console.log('Ending session with final score:', score);
    setSessionActive(false);
    setSessionEnded(true);
    setCurrentActivity(null);
  };

  const handleBack = () => {
    setCurrentActivity(null);
    // Only reset session if global timer is not enabled
    if (!settings.globalTimerEnabled) {
      console.log('Resetting session (no global timer)');
      setSessionActive(false);
    } else {
      console.log('Keeping session active (global timer enabled)');
    }
  };

  const CurrentActivityComponent = currentActivity
    ? activities.find((activity) => activity.id === currentActivity)?.component
    : null;

  const { rows, cols } = getGridLayout(activities.length);

  // Session end screen
  if (sessionEnded) {
    return (
      <Container>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h4" gutterBottom>Session terminée!</Typography>
          <Typography variant="h5" gutterBottom>Score final: {score} ⭐</Typography>
          <Button 
            variant="contained" 
            onClick={() => {
              setSessionEnded(false);
              setScore(0);
            }}
            sx={{ mt: 2 }}
          >
            Retour au menu
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Container>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2 
        }}>
          <Typography variant="h5">
            Les Mathématiques en Jouant
          </Typography>
          <IconButton 
            onClick={onSettingsClick}
            sx={{ 
              bgcolor: 'rgba(0, 0, 0, 0.08)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.12)',
              }
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Box>

        {currentActivity === null ? (
          <>
            {!sessionActive && settings.globalTimerEnabled && (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStartSession}
                  sx={{ mb: 2 }}
                >
                  Commencer une nouvelle session
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Durée de la session: {settings.globalTimerValue} secondes
                </Typography>
              </Box>
            )}
            <Grid
              container
              spacing={1}
              justifyContent="center"
              sx={{ maxWidth: '500px', margin: '0 auto' }}
            >
              {activities.map((activity) => (
                <Grid item xs={12 / cols} sm={6} md={12 / cols} key={activity.id}>
                  <Button
                    variant="contained"
                    onClick={() => setCurrentActivity(activity.id)}
                    disabled={settings.globalTimerEnabled && !sessionActive}
                    sx={{
                      bgcolor: activity.color,
                      color: 'white',
                      aspectRatio: '1 / 1',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      width: '100%',
                      height: '100%',
                      padding: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      '&:hover': {
                        bgcolor: activity.color,
                        filter: 'brightness(1.1)',
                      },
                      '&.Mui-disabled': {
                        bgcolor: `${activity.color}88`,
                        color: 'white',
                      },
                    }}
                  >
                    <Typography variant="h4">{activity.emoji}</Typography>
                    <Typography variant="body1" align="center">{activity.title}</Typography>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <div>
            <CurrentActivityComponent 
              onBack={handleBack}
              onCorrect={incrementScore}
              initialTimer={settings.localTimerEnabled ? settings.localTimerValue : null}
              globalTimer={settings.globalTimerEnabled && sessionActive ? settings.globalTimerValue : null}
              onGlobalTimeUp={handleSessionEnd}
              score={score}
            />
          </div>
        )}
      </Container>
    </DndProvider>
  );
}

export default ActivityPage;
